import { DocumentCategory } from "@common/types/documentCategory";
import { Form, FormikProvider, useFormik } from "formik";
import { useMemo } from "react";
import { z } from "zod";
import { createDocumentCategoryBodySchema } from "@common/schemas/documentCategory/document-category.schema";
import { Picker } from "@design-system/Picker/Picker.lib";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { isUncategorizedDocumentTaxonomy } from "@common/helpers/documentCategoryFilter";
import {
    useCreateDocumentCategoryMutation,
    useDocumentCategories,
} from "@api/documentCategory.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { DocumentCategoryIcon } from "../../documents/DocumentCategoryIcon";
import styles from "./CreateDocumentCategoryModal.module.scss";

type CreateDocumentCategoryFormSchema = z.infer<
    typeof createDocumentCategoryBodySchema
>;

const fields = createFormikFields<CreateDocumentCategoryFormSchema>();

interface CreateDocumentCategoryModalProps {
    onHide: () => void;
    parentCategoryId?: string;
    categoryName?: string;
    onCategoryCreated?: (category: DocumentCategory) => void;
}

function useTopLevelDocumentCategoryData(
    parentCategoryIdProp: string | undefined,
) {
    const { data: documentCategoriesData } = useDocumentCategories();
    const topLevelCategories: DocumentCategory[] = useMemo(
        () =>
            (documentCategoriesData?.categories ?? []).filter(
                (c: DocumentCategory) =>
                    c.parentCategoryId === null &&
                    !isUncategorizedDocumentTaxonomy(c.documentTaxonomy),
            ),
        [documentCategoriesData?.categories],
    );
    const hasTopLevelCategories = topLevelCategories.length > 0;

    const initialParentCategoryId = useMemo(() => {
        if (!hasTopLevelCategories) {
            return "";
        }
        if (parentCategoryIdProp == null) {
            return topLevelCategories[0].id;
        }
        const isTopLevel = topLevelCategories.some(
            (c: DocumentCategory) => c.id === parentCategoryIdProp,
        );
        return isTopLevel
            ? parentCategoryIdProp
            : (topLevelCategories[0]?.id ?? parentCategoryIdProp);
    }, [parentCategoryIdProp, topLevelCategories, hasTopLevelCategories]);

    return {
        topLevelCategories,
        hasTopLevelCategories,
        initialParentCategoryId,
    };
}

const CreateDocumentCategoryEmptyState = ({
    onHide,
}: Readonly<{ onHide: () => void }>) => (
    <>
        <StandardModalHeader>Create subtype</StandardModalHeader>
        <p className="mb-0">
            No parent types available. Types may still be loading.
        </p>
        <HStack as="footer" justify="end" gap={12} className="mt-3">
            <Button
                variant="secondary"
                size="xl"
                type="button"
                onClick={onHide}
            >
                Cancel
            </Button>
        </HStack>
    </>
);

export const CreateDocumentCategoryModal = ({
    parentCategoryId: parentCategoryIdProp,
    categoryName,
    onCategoryCreated,
    onHide,
}: CreateDocumentCategoryModalProps) => {
    const createMutation = useCreateDocumentCategoryMutation();
    const {
        topLevelCategories,
        hasTopLevelCategories,
        initialParentCategoryId,
    } = useTopLevelDocumentCategoryData(parentCategoryIdProp);

    const categoryItems = useMemo(
        () =>
            topLevelCategories.map((category) => ({
                value: category.id,
                label: category.label,
                leading: (
                    <DocumentCategoryIcon
                        documentTaxonomy={category.documentTaxonomy}
                    />
                ),
            })),
        [topLevelCategories],
    );

    const formik = useFormik<CreateDocumentCategoryFormSchema>({
        initialValues: {
            label: categoryName ?? "",
            description: "",
            parentCategoryId: initialParentCategoryId,
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(createDocumentCategoryBodySchema),
        onSubmit: submitHelper({
            loading: createMutation.isPending,
            handler: async (data) => {
                const createdCategory = await createMutation.mutateAsync({
                    description: data.description ?? null,
                    label: data.label,
                    parentCategoryId: data.parentCategoryId,
                });
                onCategoryCreated?.(createdCategory);
                onHide();
            },
        }),
        validateOnMount: true,
    });

    const selectedParentCategory = useMemo(
        () =>
            topLevelCategories.find(
                (c: DocumentCategory) =>
                    c.id === formik.values.parentCategoryId,
            ),
        [topLevelCategories, formik.values.parentCategoryId],
    );

    if (!hasTopLevelCategories) {
        return <CreateDocumentCategoryEmptyState onHide={onHide} />;
    }

    return (
        <>
            <StandardModalHeader>Create subtype</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16}>
                        <VStack gap={16}>
                            <FormikField<string>
                                name="parentCategoryId"
                                label="Parent type"
                                control="button"
                            >
                                <Picker.Select
                                    items={categoryItems}
                                    searchable
                                    value={selectedParentCategory?.id ?? null}
                                    onValueChange={(id) => {
                                        if (id !== null) {
                                            void formik.setFieldValue(
                                                "parentCategoryId",
                                                id,
                                            );
                                        }
                                    }}
                                    renderValue={() =>
                                        selectedParentCategory ? (
                                            <span className={styles.trigger}>
                                                <DocumentCategoryIcon
                                                    documentTaxonomy={
                                                        selectedParentCategory.documentTaxonomy
                                                    }
                                                />
                                                <span>
                                                    {
                                                        selectedParentCategory.label
                                                    }
                                                </span>
                                            </span>
                                        ) : undefined
                                    }
                                    placeholder="Select parent type"
                                    searchPlaceholder="Search..."
                                    emptyLabel="No types"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="xl"
                                            block
                                        />
                                    }
                                />
                            </FormikField>
                            <fields.Text name="label" label="Name" size="xl" />
                        </VStack>
                        <fields.Text
                            name="description"
                            label="Description"
                            optional
                            size="xl"
                        />
                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                disabled={createMutation.isPending}
                                onClick={onHide}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                loading={createMutation.isPending}
                                type="submit"
                                disabled={!formik.isValid}
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Create subtype",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                }}
                            >
                                Create
                                {onCategoryCreated ? " and assign" : ""}
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </>
    );
};
